import { type CapRule, checkCaps, type CountedText } from './caps.js';
import {
  type LocationData,
  type MerchantLocation,
  type MerchantLocationInput,
  resolveLocation,
} from './location.js';
import { assembleRules, type OverrideRow, type StateRuleRow } from './rules.js';
import { checkSendingWindow, type MessageClass, type WindowClosedReason } from './window.js';

/**
 * One compliance decision for one text (PLAN.md §4.6): where the merchant is, which rules apply, whether
 * the window is open, whether a cap is reached. Pure; the pacer and the UI previews share it. The caller
 * supplies the counted texts only when `capsToCount` says a cap applies (the pacer reads them from the
 * database inside its transaction).
 */

export interface ComplianceData extends LocationData {
  stateRules: ReadonlyMap<string, StateRuleRow>;
  /** Federal holidays, observed and actual dates (YYYY-MM-DD). */
  closedDates: ReadonlySet<string>;
}

export type HoldReason = WindowClosedReason | 'timezone_unknown' | 'state_cap';

export type ComplianceDecision =
  | { ok: true; location: Extract<MerchantLocation, { ok: true }>; closesAt: Date }
  | { ok: false; action: 'block'; reason: 'outside_us' }
  | {
      ok: false;
      action: 'hold';
      reason: HoldReason;
      /** When to look again; null when no opening was found in the next two weeks. */
      until: Date | null;
      location: Extract<MerchantLocation, { ok: true }> | null;
      cap?: { rule: CapRule; count: number };
    };

export interface ComplianceInput {
  class: MessageClass;
  merchant: MerchantLocationInput;
  data: ComplianceData;
  overrides: readonly OverrideRow[];
  now: Date;
  marginMs?: number;
}

/** Location, rules and window. `caps` lists the caps that apply to this text's class. */
export function evaluateWindow(input: ComplianceInput): {
  decision: ComplianceDecision;
  caps: CapRule[];
} {
  const location = resolveLocation(input.merchant, input.data);
  if (!location.ok) {
    if (location.action === 'block') return { decision: location, caps: [] };
    return {
      decision: { ok: false, action: 'hold', reason: location.reason, until: null, location: null },
      caps: [],
    };
  }
  const rules = assembleRules(location.states, input.data.stateRules, input.overrides);
  const win = checkSendingWindow({
    class: input.class,
    zones: location.zones,
    tightenings: rules.tightenings,
    closedDates: input.data.closedDates,
    now: input.now,
    ...(input.marginMs === undefined ? {} : { marginMs: input.marginMs }),
  });
  const caps = rules.caps.filter((c) => c.appliesTo.includes(input.class));
  if (!win.open) {
    return {
      decision: { ok: false, action: 'hold', reason: win.reason, until: win.nextOpen, location },
      caps,
    };
  }
  return { decision: { ok: true, location, closesAt: win.closesAt }, caps };
}

/** The whole decision, caps included (`texts`: the merchant's counted texts of the last 24 hours). */
export function evaluateCompliance(
  input: ComplianceInput,
  texts: readonly CountedText[],
): ComplianceDecision {
  const { decision, caps } = evaluateWindow(input);
  if (!decision.ok || caps.length === 0) return decision;
  const cap = checkCaps(input.class, caps, texts, input.now);
  if (cap.ok) return decision;
  return {
    ok: false,
    action: 'hold',
    reason: 'state_cap',
    until: cap.until,
    location: decision.location,
    cap: { rule: cap.rule, count: cap.count },
  };
}
